import { isForbiddenPathDuringImpersonation } from "../supportMode/supportModeSession";
import { getExperience } from "./experiences/registry";

export function tenantHasCapability(tenant, capability) {
  if (!capability) return true;
  if (!tenant || !Array.isArray(tenant.capabilities)) return false;
  return tenant.capabilities.includes(capability);
}

/**
 * Whether pathname is allowed for this tenant bootstrap + experience.
 * Fail closed for unknown kind, missing capability, or wrong product surface.
 *
 * Experience routeRules (original approved MA paths) are authoritative.
 * Bootstrap navigation is not used as an allowlist — it previously pointed
 * at invented /music-provider and /installer shells.
 */
export function isPathAllowedForTenant(tenant, pathname) {
  if (!tenant || !tenant.kind) {
    return { allowed: false, reason: "missing-tenant" };
  }

  const experience = getExperience(tenant.kind);
  if (!experience) {
    return { allowed: false, reason: "unknown-kind" };
  }

  if (isForbiddenPathDuringImpersonation(pathname)) {
    return { allowed: false, reason: "impersonation-forbidden" };
  }

  const normalized = pathname.split("?")[0].replace(/\/$/, "") || "/";

  // Explicitly reject invented vendor shells from the prior regression.
  if (
    normalized === "/music-provider" ||
    normalized.startsWith("/music-provider/") ||
    normalized === "/installer" ||
    normalized.startsWith("/installer/")
  ) {
    return { allowed: false, reason: "path-not-in-experience" };
  }

  // Client and installer must never reach Music Provider upload pages.
  if (
    tenant.kind !== "music-vendor" &&
    (normalized === "/master-account/musicuploader" ||
      normalized.startsWith("/master-account/musicuploader/") ||
      normalized === "/master-account/musichistory" ||
      normalized.startsWith("/master-account/musichistory/"))
  ) {
    return { allowed: false, reason: "path-not-in-experience" };
  }

  const rules = [...(experience.routeRules || [])].sort(
    (a, b) => (b.prefix || "").length - (a.prefix || "").length,
  );
  for (const rule of rules) {
    const prefix = rule.prefix.replace(/\/$/, "") || "/";
    if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
      if (rule.capability && !tenantHasCapability(tenant, rule.capability)) {
        return {
          allowed: false,
          reason: "missing-capability",
          capability: rule.capability,
        };
      }
      return { allowed: true, via: "route-rule", prefix: rule.prefix };
    }
  }

  return { allowed: false, reason: "path-not-in-experience" };
}

export function getLandingPathForTenant(tenant) {
  const experience = getExperience(tenant?.kind);
  if (!experience) return null;
  return experience.landingPath;
}
